import { ReactNode, useEffect } from 'react';
import { z } from 'zod';

import { LoadUtil } from '@suntex.core/animation';

import { usePersistedState } from '../hooks/usePersistedState';
import { SoundContext } from '../sound-context';

export interface SoundProviderProps {
  children: ReactNode;
  /**
   * Optional callback invoked whenever the sound-disabled state changes.
   * Use this to bridge the mute state into legacy or external audio systems
   * that are not wired to `createjs.Sound.muted` directly.
   */
  onSoundToggle?: (disabled: boolean) => void;
  /**
   * Seed value used when no persisted preference exists for the current user.
   * A stored localStorage value always takes precedence over this default.
   */
  initialSoundDisabled?: boolean;
}

export function SoundProvider({
  children,
  onSoundToggle,
  initialSoundDisabled = false,
}: SoundProviderProps) {
  const [soundDisabled, setSoundDisabled] = usePersistedState(
    'soundDisabled',
    z.boolean().catch(initialSoundDisabled),
  );

  useEffect(() => {
    LoadUtil.enableAudioContextUnlock();
  }, []);

  useEffect(() => {
    if (soundDisabled) LoadUtil.mute();
    else LoadUtil.unmute();
  }, [soundDisabled]);

  useEffect(() => {
    onSoundToggle?.(soundDisabled);
  }, [soundDisabled, onSoundToggle]);

  return (
    <SoundContext.Provider value={{ soundDisabled, setSoundDisabled }}>
      {children}
    </SoundContext.Provider>
  );
}
